import { useTranslation } from 'react-i18next';
import {
  type ClientLoaderFunctionArgs,
  type ShouldRevalidateFunctionArgs,
  useActionData,
  useNavigation,
  useSubmit,
  data,
  redirectDocument,
  useLocation,
  Link
} from 'react-router';

import CatalogList from '~/features/catalog/components/CatalogList';
import { CatalogViewSwitcher } from '~/features/catalog/components/CatalogViewSwitcher';
import { saveWebshopSetting } from '~/shell/customer.server';
import { CircularProgress } from '~/common/components/CircularProgress';
import CatalogTiles from '~/features/catalog/components/CatalogTiles';
import { SearchFilter } from '~/features/catalog/components/filter/SearchFilter';
import { useArticleSearchQueryParams } from '~/features/catalog/components/filter/useArticleSearchQueryParam';
import { SETTINGS } from '~/shell/types/settings';
import { useSpinDelay } from 'spin-delay';
import { FORM_FIELDS } from '~/common/constants/formfields';
import { FilterReset } from '~/features/catalog/components/filter/FilterReset';
import type { ISearchRequestPagingDTOData } from '~/dtos/ProdegaApiWebshop-api-typings-generated';
import { formatNumber } from '~/common/utils/numberUtil';
import { optionalProtectedAction, optionalProtectedLoader, AreaRedirectBehaviour } from '~/shell/utils/authUtil.server';
import { anonymousSettingsCookie } from '~/shell/cookies';
import { settingsCookie } from '~/shell/cookies.server.ts';
import { useBasePath } from '~/common/hooks/useBasePath';
import { DefaultErrorBoundary } from '~/shell/components/DefaultErrorBoundary';
import type { Route } from './+types/CatalogIndexRoute';
import { catalogPagingStorageClient } from '~/common/catalogPagingStorageClient.client';
import { CatalogLoadMoreButton } from '~/features/catalog/components/CatalogLoadMoreButton';
import { useEffect, useMemo } from 'react';
import debounce from 'lodash/debounce';
import { catalogScrollPosStorageClient } from '~/common/catalogScrollPosStorageClient.client';
import { LatestNavigation } from '~/features/catalog/components/navigation/LatestNavigation';
import type { ArticlePage } from '~/common/types/articlePage';
import { LoadingIndicator } from '~/common/components/LoadingIndicator';
import { PgArea, TgArea } from '~/common/types/area';
import { mergeRouteModuleMeta } from '~/shell/utils/metaUtil';
import { OwnBrandsNavigation } from '~/features/catalog/components/navigation/OwnBrandsNavigation';
import { buildCatalogSearchQuery, CATALOG_SEARCH_KEY } from '~/features/catalog/utils/catalogSearchKeyUtil.ts';
import { searchArticles } from '~/features/catalog/searchLoader.server.ts';
import { getQueryClient } from '~/queryClient.ts';
import { CatalogIndexBreadcrumbs } from '~/common/components/CatalogIndexBreadcrumbs.tsx';
import { useCatalogSelectionInfo } from '~/features/catalog/hooks/useCatalogSelectionInfo.ts';
import { Media } from '~/media.ts';
import { ClientOnly } from 'remix-utils/client-only';
import { useUiContext } from '~/common/components/context/useUiContext.ts';
import { useRootLoaderData } from '~/common/utils/useRootLoaderData';
import { getLogger } from '~/logging/logger.ts';
import { logToConsole } from '~/logging/consoleLogger';
import { useSendSearchToGoogleAnalyticsEffect } from '~/tracking/googleAnalytics.ts';

const ACTION_INTENT = {
  UpdateViewType: 'updateViewType'
};

export const shouldRevalidate = ({ formAction, formData, defaultShouldRevalidate }: ShouldRevalidateFunctionArgs) => {
  // Do not revalidate when adding an article to the cart
  if (formAction?.includes('resources/cart/add')) {
    return false;
  }

  if (formData?.has(FORM_FIELDS.Intent) && formData.get(FORM_FIELDS.Intent) === ACTION_INTENT.UpdateViewType) {
    return false;
  }

  return defaultShouldRevalidate;
};

export const loader = optionalProtectedLoader(
  async ({ request, url, loginInfo, lang, t, area, api }) => {
    return await searchArticles(api, request, url, area, loginInfo ?? null, lang, t);
  },
  { areaRedirectBehavior: AreaRedirectBehaviour.Auto }
);

const loadPagedArticles = async (key: string, currentPage: number): Promise<ArticlePage[] | null> => {
  try {
    const loadedArticlePages = await catalogPagingStorageClient.getItem(key);

    if (!loadedArticlePages) {
      if (currentPage === 0) {
        return [];
      } else {
        return null;
      }
    }

    try {
      const parsedArticlePages = loadedArticlePages as ArticlePage[];
      const previousArticlePages = parsedArticlePages.filter(page => page.page < currentPage);
      // Check the array if a every previous page is already loaded
      const requiredPreviousPages = Array.from({ length: currentPage }, (_, index) => index);
      for (const requiredPage of requiredPreviousPages) {
        if (!previousArticlePages.some(page => page.page === requiredPage)) {
          return null;
        }
      }

      return previousArticlePages;
    } catch {
      return [];
    }
  } catch {
    return [];
  }
};

const savePagedArticles = async (key: string, articlePages: ArticlePage[]) => {
  try {
    await catalogPagingStorageClient.clear();
    await catalogPagingStorageClient.setItem(key, articlePages, {});
  } catch (error) {
    logToConsole({
      loggerName: 'catalog-index-route',
      logLevel: 'ERROR',
      message: 'Error saving article pages to storage',
      args: [error]
    });
  }
};

export const clientLoader = async ({ serverLoader, request }: ClientLoaderFunctionArgs) => {
  const url = new URL(request.url);
  const urlSearchParams = new URLSearchParams(url.search);

  const cacheKey = buildCatalogSearchQuery(url.pathname, urlSearchParams);

  const queryClient = getQueryClient();
  let searchResult: any = queryClient.getQueryData(cacheKey);

  if (!searchResult) {
    searchResult = await serverLoader();

    queryClient.setQueryData(cacheKey, searchResult, {
      updatedAt: Date.now()
    });

    queryClient.setQueryDefaults(cacheKey, {
      staleTime: 1000 * 60 * 5
    });
  }

  urlSearchParams.delete('page');
  const storageKey = 'catalogArticlePages' + urlSearchParams.toString();

  const articlePages = await loadPagedArticles(storageKey, searchResult.searchResponse.page);

  if (articlePages === null) {
    const redirectUrl = new URL(window.location.href);
    redirectUrl.searchParams.set('page', '0');
    throw redirectDocument(redirectUrl.pathname + redirectUrl.search);
  } else {
    const page = searchResult.searchResponse.page;
    const articles = searchResult.searchResponse.articles;
    articlePages.push({ page, articles });
    await savePagedArticles(storageKey, articlePages);

    return {
      ...searchResult,
      articles: articlePages.flatMap(page => page.articles)
    };
  }
};

export function HydrateFallback() {
  return <LoadingIndicator />;
}

clientLoader.hydrate = true as const;

export const action = optionalProtectedAction(async ({ request, loginInfo, api }) => {
  const cookieHeaderValue = request.headers.get('cookie');

  const headers = new Headers();

  const formData = await request.formData();
  const intent = formData.get(FORM_FIELDS.Intent);

  if (intent === ACTION_INTENT.UpdateViewType) {
    const showTileView = formData.get(SETTINGS.tileView) === 'true';

    // Save the setting only if the user is authenticated
    if (loginInfo) {
      const savedSettings = await saveWebshopSetting(api, SETTINGS.tileView, showTileView);

      // Update the settings in the settingsCookie
      if (savedSettings.settings) {
        const cookieHeader = await settingsCookie.serialize(savedSettings.settings);
        headers.append('Set-Cookie', cookieHeader);
      }
    } else {
      const anonymousSettings = await anonymousSettingsCookie.parse(cookieHeaderValue);
      const newSettings = { ...anonymousSettings, tileView: showTileView };
      const cookieHeader = await anonymousSettingsCookie.serialize(newSettings);
      headers.append('Set-Cookie', cookieHeader);
    }

    return data(
      {
        isAuthenticated: !!loginInfo,
        showTileView: showTileView
      },
      { headers }
    );
  }

  throw new Error('Invalid intent');
});

export const handle = {
  // The navigation items for the current route. Will be rendered in the sub navigation area.
  subNavigationItems: () => {
    return (
      <>
        <OwnBrandsNavigation className={'mt-12'} />
        <LatestNavigation className={'mt-12'} />
      </>
    );
  }
};

export const meta: Route.MetaFunction = mergeRouteModuleMeta(({ loaderData }) => {
  return loaderData?.meta ?? [];
});

export default function CatalogIndexRoute({ loaderData }: Route.ComponentProps) {
  const log = getLogger('catalog-index-route');
  const { t } = useTranslation();
  const {
    originalSearchTerm,
    searchTerm,
    searchResponse,
    showTileView,
    liveSearchRequest,
    filters,
    isTgArea,
    isAuthenticated,
    articles
  } = loaderData;
  const navigation = useNavigation();
  const { setSearchQueryParam } = useArticleSearchQueryParams();
  const actionData = useActionData<typeof action>();
  const submit = useSubmit();
  const { i18n } = useTranslation();
  const { path } = useBasePath();
  const location = useLocation();
  const effectiveSearchTerm = searchResponse.searchTerm ?? searchTerm;
  useSendSearchToGoogleAnalyticsEffect(effectiveSearchTerm, searchResponse.totalCount);

  const handViewTypeChange = (showTileView: boolean) => {
    const queryClient = getQueryClient();
    queryClient.removeQueries({ queryKey: [CATALOG_SEARCH_KEY], exact: false });

    const formData = new FormData();
    formData.set(FORM_FIELDS.Intent, ACTION_INTENT.UpdateViewType);
    formData.set(SETTINGS.tileView, showTileView.toString());
    submit(formData, { method: 'POST' });
  };

  const handleApplyFilter = (searchRequest: ISearchRequestPagingDTOData) => {
    setIsSearchFilterInProgress(true);
    setSearchQueryParam(searchRequest);
  };

  const scrollKey = useMemo(() => {
    const urlSearchParams = new URLSearchParams(location.search);
    urlSearchParams.delete('page');
    return `scroll-pos:${urlSearchParams.toString()}`;
  }, [location.search]);

  useEffect(() => {
    const handleScroll = async () => {
      await catalogScrollPosStorageClient.clear();
      if (window.scrollY > 0) {
        await catalogScrollPosStorageClient.setItem(scrollKey, String(':' + window.scrollY));
      }
    };

    const debouncedHandleScroll = debounce(handleScroll, 500);
    window.addEventListener('scroll', debouncedHandleScroll);

    return () => {
      window.removeEventListener('scroll', debouncedHandleScroll);
    };
  }, [location, scrollKey]);

  useEffect(() => {
    let isActive = true; // Prevents the scroll position from being restored after the component has been unmounted

    const handleScrollPositionRestore = async () => {
      let savedPos = await catalogScrollPosStorageClient.getItem(scrollKey);
      if (isActive && savedPos && typeof savedPos === 'string') {
        const searchKey = ':';
        if (savedPos.startsWith(searchKey)) {
          savedPos = savedPos.replace(searchKey, '');
          if (isActive) {
            window.scrollTo(0, Number(savedPos));
          }
          // Send the event twice to ensure the scroll position is restored on initial render
          setTimeout(() => {
            if (isActive) {
              window.scrollTo(0, Number(savedPos));
            }
          }, 50);
        }
      } else {
        log.debug('No scroll position saved');
      }
    };

    handleScrollPositionRestore();

    return () => {
      isActive = false;
    };
  }, [articles, scrollKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const { isSearchFilterInProgress, setIsSearchFilterInProgress } = useUiContext();
  const isLoading = navigation.state === 'loading' && navigation.location.pathname === `${path}/catalog`;
  const isSearchFilterDisabled = isSearchFilterInProgress || isLoading;
  const showProgress = useSpinDelay(isLoading, { delay: 200, minDuration: 800 });
  const tileView = actionData ? actionData.showTileView : showTileView;
  const hasResults = articles.length > 0;
  const rootLoaderData = useRootLoaderData();
  const { agr, hagr, hwg } = useCatalogSelectionInfo(rootLoaderData.catalogItems?.items ?? []);

  // Flag zurücksetzen, sobald die Navigation wieder idle ist. Der kleine Delay
  // verhindert Flicker: falls die Navigation erst im nächsten Tick startet,
  // wird der Timer via Cleanup gecancelt und der Flag bleibt korrekt true.
  useEffect(() => {
    if (!isSearchFilterInProgress || navigation.state !== 'idle') return;
    const timer = setTimeout(() => setIsSearchFilterInProgress(false), 50);
    return () => clearTimeout(timer);
  }, [isSearchFilterInProgress, navigation.state, setIsSearchFilterInProgress]);

  return (
    <div className={'desktop:p-0 p-4'}>
      <CatalogIndexBreadcrumbs />

      <h1 data-testid={'page-title'} className=" desktop:mt-4">
        {searchTerm && searchTerm?.length > 0 ? (
          <>
            {t('Ergebnisse für')}&nbsp;<b>&quot;{searchResponse.searchTerm}&quot;</b>
          </>
        ) : (
          (agr?.name ?? hagr?.name ?? hwg?.name ?? t('Gesamtsortiment'))
        )}
      </h1>

      {originalSearchTerm && originalSearchTerm !== searchResponse.searchTerm && (
        <div className={'my-6'} data-testid={'catalog-no-results-text'}>
          {t('Keine Ergebnisse gefunden für')}&nbsp;<b>&#34;{originalSearchTerm}&#34;</b>
        </div>
      )}

      {searchResponse.didYouMeanTerms?.length > 0 && !liveSearchRequest.hagrIds && !liveSearchRequest.agrIds && (
        <div className={'my-6 flex'}>
          <b>{t('Meinten Sie')}:</b>&nbsp;
          {searchResponse.didYouMeanTerms?.map((d, index) => {
            return (
              <div key={d}>
                <Link
                  to={`${path}/catalog?searchTerm=${d}`}
                  className={'text-primary font-bold mx-0.5'}
                  data-testid={`didyoumean-link-${d}`}
                >
                  {d}
                </Link>
                {index < searchResponse.didYouMeanTerms.length - 1 && <span>|</span>}
              </div>
            );
          })}
        </div>
      )}
      <SearchFilter
        className={'my-4'}
        liveSearchRequest={liveSearchRequest}
        totalCount={articles.length}
        onApplyFilter={handleApplyFilter}
        filters={filters}
        area={isTgArea ? TgArea : PgArea}
        disabled={isSearchFilterDisabled}
      />
      {hasResults && (
        <div className={'flex justify-between items-end mb-2'}>
          <div className="flex flex-row items-center gap-2">
            <div className="text-sm text-secondary" data-testid={'catalog-result-count'}>
              {`${formatNumber(searchResponse.totalCount, i18n.language)} ${t(
                searchResponse.totalCount === 1 ? 'Produkt' : 'Produkte'
              )}`}
            </div>
            {showProgress && <CircularProgress size={'sm'} />}
            <FilterReset filters={filters} liveSearchRequest={liveSearchRequest} />
          </div>
          <CatalogViewSwitcher
            className={'hidden desktop:block'}
            showTileView={tileView}
            onViewTypeChange={handViewTypeChange}
          />
        </div>
      )}
      {hasResults && <hr />}
      <Media lessThan={'desktop'}>
        <CatalogTiles articleType={isTgArea ? 'bgh' : 'agh'} isAuthenticated={isAuthenticated} articles={articles} />
      </Media>
      <Media greaterThanOrEqual={'desktop'}>
        {tileView ? (
          <CatalogTiles articleType={isTgArea ? 'bgh' : 'agh'} isAuthenticated={isAuthenticated} articles={articles} />
        ) : (
          <CatalogList articleType={isTgArea ? 'bgh' : 'agh'} articles={articles} isAuthenticated={isAuthenticated} />
        )}
      </Media>

      {!hasResults && <div className="text-2xl text-center mt-24">{t('Keine Artikel für diese Suche!')}</div>}

      {hasResults && (
        <ClientOnly>
          {() => (
            <CatalogLoadMoreButton
              pageSize={searchResponse.pageSize}
              page={searchResponse.page}
              totalCount={searchResponse.totalCount}
              includeSearchParamInSubmit={true}
            />
          )}
        </ClientOnly>
      )}
    </div>
  );
}

export function ErrorBoundary() {
  return <DefaultErrorBoundary />;
}
